import React, { useState } from 'react';
import { Typography, Card, Row, Col, Breadcrumb, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  FileTextOutlined,
  BankOutlined,
  DollarOutlined,
  ToolOutlined,
  ShopOutlined,
  BarChartOutlined,
  ArrowLeftOutlined,

} from '@ant-design/icons';

const { Title, Text } = Typography;

interface FolderItem {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  type: 'folder' | 'file';
  path: string[];
  children?: FolderItem[];
}

export default function AdministrationDocumentsTab() {
  const { t } = useTranslation(['documents']);
  const [currentFolder, setCurrentFolder] = useState<FolderItem | null>(null);
  const [currentPath, setCurrentPath] = useState<string[]>([]);

  // Function to create responsive folder icons
  const createFolderIcon = (backgroundColor: string, borderColor: string, icon: React.ReactNode) => (
    <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
      <div style={{ 
        width: '100%', 
        maxWidth: '80px',
        height: 'auto',
        aspectRatio: '4/3',
        backgroundColor, 
        borderRadius: '4px 4px 0 0',
        position: 'relative',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        border: `1px solid ${borderColor}`,
        margin: '0 auto'
      }}>
        {/* Folder tab */}
        <div style={{
          position: 'absolute',
          top: '-8px',
          left: '8px',
          width: 'clamp(24px, 8vw, 32px)',
          height: 'clamp(12px, 4vw, 16px)',
          backgroundColor,
          borderRadius: '4px 4px 0 0',
          border: `1px solid ${borderColor}`,
          borderBottom: 'none'
        }} />
        {/* Icon overlay */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: 'clamp(14px, 4vw, 20px)',
          color: 'inherit'
        }}>
          {icon}
        </div>
      </div>
    </div>
  );

  const rootFolders: FolderItem[] = [
    {
      id: 'finance',
      name: t('documents:folders.finance'),
      icon: createFolderIcon('#ffd700', '#d4af37', <DollarOutlined style={{ color: '#52c41a' }} />),
      description: t('documents:folders.descriptions.finance'),
      type: 'folder',
      path: [t('documents:folders.finance')],
      children: [
        {
          id: 'payables',
          name: t('documents:folders.payables'),
          icon: createFolderIcon('#ffd700', '#d4af37', <FileTextOutlined style={{ color: '#ff4d4f' }} />),
          description: t('documents:folders.descriptions.payables'),
          type: 'folder',
          path: [t('documents:folders.finance'), t('documents:folders.payables')]
        },
        {
          id: 'receivables',
          name: t('documents:folders.receivables'),
          icon: createFolderIcon('#ffd700', '#d4af37', <FileTextOutlined style={{ color: '#52c41a' }} />),
          description: t('documents:folders.descriptions.receivables'),
          type: 'folder',
          path: [t('documents:folders.finance'), t('documents:folders.receivables')]
        },
        {
          id: 'accounting',
          name: t('documents:folders.accounting'),
          icon: createFolderIcon('#ffd700', '#d4af37', <FileTextOutlined style={{ color: '#1890ff' }} />),
          description: t('documents:folders.descriptions.accounting'),
          type: 'folder',
          path: [t('documents:folders.finance'), t('documents:folders.accounting')]
        }
      ]
    },
    {
      id: 'banking',
      name: t('documents:folders.banking'),
      icon: createFolderIcon('#87ceeb', '#5f9ea0', <BankOutlined style={{ color: '#1890ff' }} />),
      description: t('documents:folders.descriptions.banking'),
      type: 'folder',
      path: [t('documents:folders.banking')],
      children: [
        {
          id: 'transactions',
          name: t('documents:folders.transactions'),
          icon: createFolderIcon('#87ceeb', '#5f9ea0', <FileTextOutlined style={{ color: '#52c41a' }} />),
          description: t('documents:folders.descriptions.transactions'),
          type: 'folder',
          path: [t('documents:folders.banking'), t('documents:folders.transactions')]
        },
        {
          id: 'statements',
          name: t('documents:folders.statements'),
          icon: createFolderIcon('#87ceeb', '#5f9ea0', <FileTextOutlined style={{ color: '#52c41a' }} />),
          description: t('documents:folders.descriptions.statements'),
          type: 'folder',
          path: [t('documents:folders.banking'), t('documents:folders.statements')]
        }
      ]
    },
    {
      id: 'assets',
      name: t('documents:folders.assets'),
      icon: createFolderIcon('#98fb98', '#90ee90', <ToolOutlined style={{ color: '#13c2c2' }} />),
      description: t('documents:folders.descriptions.assets'),
      type: 'folder',
      path: [t('documents:folders.assets')],
      children: [
        {
          id: 'warranties',
          name: t('documents:folders.warranties'),
          icon: createFolderIcon('#98fb98', '#90ee90', <FileTextOutlined style={{ color: '#52c41a' }} />),
          description: t('documents:folders.descriptions.warranties'),
          type: 'folder',
          path: [t('documents:folders.assets'), t('documents:folders.warranties')]
        },
        {
          id: 'purchases',
          name: t('documents:folders.purchaseInvoices'),
          icon: createFolderIcon('#98fb98', '#90ee90', <FileTextOutlined style={{ color: '#1890ff' }} />),
          description: t('documents:folders.descriptions.purchaseInvoices'),
          type: 'folder',
          path: [t('documents:folders.assets'), t('documents:folders.purchaseInvoices')]
        }
      ]
    },
    {
      id: 'reports',
      name: t('documents:folders.reports'),
      icon: createFolderIcon('#dda0dd', '#ba55d3', <BarChartOutlined style={{ color: '#52c41a' }} />),
      description: t('documents:folders.descriptions.reports'),
      type: 'folder',
      path: [t('documents:folders.reports')],
      children: [
        {
          id: 'expense-reports',
          name: t('documents:folders.expenseReports'),
          icon: createFolderIcon('#dda0dd', '#ba55d3', <FileTextOutlined style={{ color: '#ff4d4f' }} />),
          description: t('documents:folders.descriptions.expenseReports'),
          type: 'folder',
          path: [t('documents:folders.reports'), t('documents:folders.expenseReports')],
          children: [
            {
              id: '2024',
              name: '2024',
              icon: createFolderIcon('#dda0dd', '#ba55d3', <FileTextOutlined style={{ color: '#1890ff' }} />),
              description: t('documents:folders.descriptions.year2024'),
              type: 'folder',
              path: [t('documents:folders.reports'), t('documents:folders.expenseReports'), '2024'],
              children: [
                {
                  id: 'january',
                  name: t('documents:folders.january'),
                  icon: createFolderIcon('#dda0dd', '#ba55d3', <FileTextOutlined style={{ color: '#52c41a' }} />),
                  description: t('documents:folders.descriptions.january'),
                  type: 'folder',
                  path: [t('documents:folders.reports'), t('documents:folders.expenseReports'), '2024', t('documents:folders.january')]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'maintenance',
      name: t('documents:folders.maintenance'),
      icon: createFolderIcon('#ffa07a', '#ff7f50', <ToolOutlined style={{ color: '#fa8c16' }} />),
      description: t('documents:folders.descriptions.maintenance'),
      type: 'folder',
      path: [t('documents:folders.maintenance')],
      children: [
        {
          id: 'contracts',
          name: t('documents:folders.contracts'),
          icon: createFolderIcon('#ffa07a', '#ff7f50', <FileTextOutlined style={{ color: '#1890ff' }} />),
          description: t('documents:folders.descriptions.contracts'),
          type: 'folder',
          path: [t('documents:folders.maintenance'), t('documents:folders.contracts')]
        }
      ]
    },
    {
      id: 'vendors',
      name: t('documents:folders.vendors'),
      icon: createFolderIcon('#f0e68c', '#daa520', <ShopOutlined style={{ color: '#eb2f96' }} />),
      description: t('documents:folders.descriptions.vendors'),
      type: 'folder',
      path: [t('documents:folders.vendors')],
      children: [
        {
          id: 'contracts',
          name: t('documents:folders.vendorContracts'),
          icon: createFolderIcon('#f0e68c', '#daa520', <FileTextOutlined style={{ color: '#1890ff' }} />),
          description: t('documents:folders.descriptions.vendorContracts'),
          type: 'folder',
          path: [t('documents:folders.vendors'), t('documents:folders.vendorContracts')]
        }
      ]
    }
  ];

  const handleFolderClick = (folder: FolderItem) => {
    if (folder.type === 'folder') {
      setCurrentPath(folder.path);
      setCurrentFolder(folder);
    }
  };

  const handleBreadcrumbClick = (path: string[]) => {
    setCurrentPath(path);
    if (path.length === 0) {
      setCurrentFolder(null);
    } else {
      const folder = findFolderByPath(rootFolders, path);
      setCurrentFolder(folder);
    }
  };

  const findFolderByPath = (folders: FolderItem[], path: string[]): FolderItem | null => {
    for (const folder of folders) {
      if (folder.path.join('/') === path.join('/')) {
        return folder;
      }
      if (folder.children) {
        const found = findFolderByPath(folder.children, path);
        if (found) return found;
      }
    }
    return null;
  };

  const getCurrentItems = (): FolderItem[] => {
    if (!currentFolder) {
      return rootFolders;
    }
    return currentFolder.children || [];
  };

  const renderFolderCard = (item: FolderItem) => (
    <Col xs={12} sm={8} md={6} lg={4} xl={3} key={item.id}>
      <Card
        hoverable
        style={{ 
          marginBottom: 16, 
          cursor: 'pointer',
          textAlign: 'center',
          height: '100%',
          minHeight: '120px'
        }}
        onClick={() => handleFolderClick(item)}
        bodyStyle={{ 
          padding: '12px 8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%'
        }}
      >
        <div style={{ marginBottom: 8, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {item.icon}
        </div>
        <div style={{ flex: 1 }}>
          <Title level={5} style={{ 
            margin: '6px 0', 
            fontSize: 'clamp(11px, 3vw, 13px)',
            lineHeight: '1.2',
            wordBreak: 'break-word'
          }}>
            {item.name}
          </Title>
          <Text type="secondary" style={{ 
            fontSize: 'clamp(9px, 2.5vw, 11px)', 
            lineHeight: '1.3',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {item.description}
          </Text>
        </div>
      </Card>
    </Col>
  );

  const renderBreadcrumb = () => {
    if (currentPath.length === 0) return null;

    const breadcrumbItems = [
      {
        title: (
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />}
            onClick={() => handleBreadcrumbClick([])}
            style={{ padding: 0 }}
          >
            {t('documents:title')}
          </Button>
        )
      },
      ...currentPath.map((path, index) => ({
        title: (
          <Button
            key={index}
            type="text"
            onClick={() => handleBreadcrumbClick(currentPath.slice(0, index + 1))}
            style={{ padding: 0 }}
          >
            {path}
          </Button>
        )
      }))
    ];

    return (
      <Breadcrumb 
        items={breadcrumbItems}
        style={{ marginBottom: 24 }}
      />
    );
  };

  return (
    <div>
      {renderBreadcrumb()}
      
      <div style={{ marginBottom: 24 }}>
        <Title level={3}>
          {currentFolder ? currentFolder.name : t('documents:tabs.administration')}
        </Title>
        {currentFolder && (
          <Text type="secondary">
            {currentFolder.description}
          </Text>
        )}
      </div>

      <Row gutter={[16, 16]}>
        {getCurrentItems().map(renderFolderCard)}
      </Row>

      {getCurrentItems().length === 0 && (
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: 16 }} />
          <Title level={4} type="secondary">
            {t('documents:noDocuments')}
          </Title>
          <Text type="secondary">
            This folder is empty. Documents will appear here when uploaded.
          </Text>
        </Card>
      )}
    </div>
  );
}
